import { ExternalLink, Send, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  buildShareIntentUrl,
  COPY_FALLBACK_PLATFORMS,
  openShareIntent,
  PLATFORM_LABELS,
  type SharePlatform,
} from "@/utils/share-intents";

interface ShareIntentButtonProps {
  platform: SharePlatform;
  text: string;
  url: string;
  /** Optional saved group/chat URL — staff-only, opens the chat directly. */
  groupUrl?: string | null;
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
}

function PlatformIcon({ platform, className }: { platform: SharePlatform; className?: string }) {
  if (platform === "whatsapp") {
    return (
      <span className={cn("inline-flex items-center justify-center -translate-x-[2px] -translate-y-[0.5px]")}>
        <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
        </svg>
      </span>
    );
  }
  if (platform === "telegram") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
      </svg>
    );
  }
  if (platform === "line") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
        <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
      </svg>
    );
  }
  if (platform === "viber") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
        <path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817c-.06 3.11-.13 8.95 5.5 10.541v2.42s-.037.97.6 1.17c.776.243 1.227-.5 1.967-1.296.405-.44.965-1.084 1.387-1.577 3.85.324 6.81-.418 7.146-.527.776-.252 5.176-.816 5.892-6.657.733-6.021-.36-9.83-2.328-11.55l-.011-.005c-.595-.55-2.987-2.295-8.317-2.316 0 0-.394-.025-1.069-.02zm.066 1.717c.572-.004.91.018.91.018 4.512.018 6.673 1.378 7.178 1.838 1.665 1.435 2.514 4.867 1.895 9.897-.598 4.88-4.166 5.19-4.821 5.4-.28.09-2.882.735-6.155.522 0 0-2.44 2.943-3.202 3.708-.119.124-.26.174-.353.151-.13-.032-.166-.187-.164-.414L7.18 18.81c-4.764-1.32-4.486-6.292-4.435-8.89.053-2.598.545-4.727 1.999-6.16 1.956-1.776 5.472-2.04 7.108-2.05zm.282 2.43c-.224 0-.224.345 0 .347 1.737.013 3.168.6 4.305 1.722 1.137 1.123 1.713 2.638 1.725 4.55.002.225.345.225.345 0v-.002c-.027-3.748-2.512-6.595-6.375-6.617zm-3.39.94a.972.972 0 0 0-.41.075h-.005C7 5.51 6.554 5.86 5.967 6.41a.708.708 0 0 0-.075.087c-.422.49-.61.97-.632 1.443a1.65 1.65 0 0 0 .057.546l.01.005c.205.74.659 1.526 1.395 2.794.474.842.949 1.679 1.527 2.464.292.397.633.81 1.066 1.244l.01.014.014.011c.434.434.85.776 1.246 1.066.785.578 1.624 1.06 2.464 1.527 1.266.735 2.057 1.19 2.798 1.395l.013.005c.18.05.353.075.522.057.482-.027.961-.214 1.45-.638.085-.018.085-.05.085-.067.55-.586.901-1.034 1.234-1.376.07-.071.107-.16.117-.252.014-.087-.018-.166-.07-.234-.13-.116-.297-.247-.493-.408a30.49 30.49 0 0 0-.595-.482c-.402-.328-.853-.7-1.295-1.072-.21-.18-.328-.18-.494-.18-.183 0-.36.06-.502.18-.214.16-.392.387-.515.563-.156.226-.31.39-.469.487-.105.066-.207.105-.297.117-.106-.014-.193-.05-.244-.106l-.046-.04-.137-.137-.328-.328a18.144 18.144 0 0 1-.766-.842c-.298-.342-.6-.706-.832-1.07a2.36 2.36 0 0 1-.106-.244l.01-.05c.107-.181.214-.337.328-.46.207-.214.42-.412.564-.515.106-.07.18-.18.214-.301a.733.733 0 0 0-.057-.495 6.96 6.96 0 0 0-.495-.604c-.273-.328-.604-.703-.93-1.07-.272-.314-.55-.62-.798-.892l-.105-.117a.69.69 0 0 0-.422-.18zm3.21.41c-.222.002-.222.34 0 .342 2.694.02 4.91 1.857 4.93 5.224.002.224.347.222.345-.003-.022-3.633-2.444-5.54-5.275-5.563zm.717 1.38c-.23-.007-.238.34-.01.347 1.46.078 2.17.81 2.252 2.323.012.225.357.207.347-.018-.092-1.69-1.014-2.557-2.59-2.652z" />
      </svg>
    );
  }
  if (platform === "messenger") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
        <path d="M.001 11.639C.001 4.949 5.241 0 12.001 0S24 4.95 24 11.639c0 6.689-5.24 11.638-12 11.638-1.21 0-2.38-.16-3.47-.46a.96.96 0 0 0-.64.05l-2.39 1.05a.96.96 0 0 1-1.35-.85l-.07-2.14a.97.97 0 0 0-.32-.68A11.39 11.39 0 0 1 0 11.639zm8.32-2.19l-3.52 5.6c-.35.53.32 1.14.82.77l3.79-2.87c.26-.2.6-.2.87 0l2.8 2.1c.84.63 2.04.4 2.6-.48l3.52-5.6c.35-.53-.32-1.14-.82-.77l-3.79 2.87c-.26.2-.6.2-.87 0l-2.8-2.1a1.85 1.85 0 0 0-2.6.48z" />
      </svg>
    );
  }
  if (platform === "wechat") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
        <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.328.328 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .55-.012.82-.035-.341-.71-.526-1.502-.526-2.342 0-3.633 3.366-6.582 7.51-6.582.213 0 .422.012.628.034C16.378 4.155 12.748 2.188 8.691 2.188zm-2.62 2.93a1.099 1.099 0 1 1 0 2.198 1.099 1.099 0 0 1 0-2.198zm5.241 0a1.1 1.1 0 1 1 0 2.198 1.1 1.1 0 0 1 0-2.198zM16.51 9.866c-3.45 0-6.252 2.524-6.252 5.638 0 3.115 2.802 5.639 6.252 5.639a7.69 7.69 0 0 0 2.241-.336.633.633 0 0 1 .587.085l1.588.928a.273.273 0 0 0 .139.046.243.243 0 0 0 .242-.243c0-.06-.024-.119-.04-.178l-.326-1.235a.495.495 0 0 1 .179-.556C22.642 18.529 24 17.034 24 15.504c0-3.114-2.802-5.638-6.253-5.638h-1.237zm-2.022 2.442a.916.916 0 1 1 0 1.832.916.916 0 0 1 0-1.832zm4.394 0a.916.916 0 1 1 0 1.832.916.916 0 0 1 0-1.832z" />
      </svg>
    );
  }
  if (platform === "kakao") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
        <path d="M12 2.4c-6.07 0-11 3.86-11 8.625 0 3.104 2.097 5.825 5.248 7.349-.215.776-.781 2.85-.894 3.292-.14.55.202.543.425.395.175-.117 2.79-1.892 3.92-2.658.762.108 1.55.165 2.301.165 6.07 0 11-3.86 11-8.543C23 6.26 18.07 2.4 12 2.4z" />
      </svg>
    );
  }
  return <Share2 className={className} aria-hidden />;
}

const PLATFORM_TONES: Record<SharePlatform, string> = {
  whatsapp: "bg-[#25D366] hover:bg-[#1ebe57] text-white",
  telegram: "bg-[#229ED9] hover:bg-[#1d8cc1] text-white",
  line: "bg-[#06C755] hover:bg-[#05b34c] text-white",
  viber: "bg-[#7360F2] hover:bg-[#5f4ce0] text-white",
  messenger: "bg-[#0084FF] hover:bg-[#0073e0] text-white",
  wechat: "bg-[#07C160] hover:bg-[#06ad55] text-white",
  kakao: "bg-[#FEE500] hover:bg-[#e8d100] text-[#3C1E1E]",
  system: "bg-muted hover:bg-muted/70 text-foreground",
};

/**
 * Coach-only quick-share button. Renders next to the existing copy/RSVP
 * segmented control. Tapping it opens the chosen messenger with the message
 * pre-filled — the coach picks the chat in the native picker.
 *
 * If a saved group URL is configured, a second small icon opens that chat
 * directly so the coach can paste the link. The group URL is staff-only and
 * is never embedded in the share message itself.
 */
export function ShareIntentButton({
  platform,
  text,
  url,
  groupUrl,
  ariaLabel,
  className,
  disabled = false,
}: ShareIntentButtonProps) {
  const tone = PLATFORM_TONES[platform];

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    if (COPY_FALLBACK_PLATFORMS.includes(platform)) {
      if (platform === "system" && typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({ text });
          return;
        } catch (err: any) {
          if (err?.name === "AbortError") return;
        }
      }
      try {
        await navigator.clipboard.writeText(text);
        const target = platform === "system" ? "your team chat" : PLATFORM_LABELS[platform];
        toast.success(`Message copied — paste into ${target}`);
      } catch {
        toast.error("Couldn't share or copy");
      }
      return;
    }

    const intentUrl = buildShareIntentUrl(platform, text, url);
    if (intentUrl) openShareIntent(intentUrl);
  };

  const handleOpenGroup = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!groupUrl) return;
    openShareIntent(groupUrl);
  };

  return (
    <div className={cn("inline-flex items-stretch rounded-xl overflow-hidden", platform !== "system" && "shadow-sm", className)}>
      <button
        type="button"
        onClick={handleShare}
        disabled={disabled}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={cn(
          "h-11 w-11 shrink-0 inline-flex items-center justify-center transition-colors",
          tone,
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        {platform === "system" ? (
          <Send className="h-5 w-5" />
        ) : (
          <PlatformIcon platform={platform} className="h-[22px] w-[22px]" />
        )}
      </button>
      {groupUrl && (
        <button
          type="button"
          onClick={handleOpenGroup}
          aria-label="Open my team chat"
          title="Open my team chat"
          className={cn(
            "h-11 w-8 shrink-0 inline-flex items-center justify-center border-s border-white/20 transition-colors",
            tone,
          )}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
